import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { LayoutDashboard, FolderKanban, Users, Receipt, FileText, UserCog, Activity, Settings, LogOut, Menu, X, Moon, Sun, Link2, Calendar, DollarSign, Clock, Calculator, Wallet, BarChart3, HandCoins, ReceiptText, CalendarClock, FileSignature, Banknote } from 'lucide-react';
import { Button } from './ui/button';
import { Trans, useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Layout() {
  const { user, logout, stopImpersonation } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  // Tablet / laptop (>= 768px): the sidebar sits beside the page and pushes it; the
  // choice is remembered. Phone: it slides over the page and closes after navigating.
  const desktopQuery = '(min-width: 768px)';
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(desktopQuery).matches);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (!window.matchMedia(desktopQuery).matches) return false;
    try { return localStorage.getItem('sidebarOpen') !== 'false'; } catch { return true; }
  });

  useEffect(() => {
    const mq = window.matchMedia(desktopQuery);
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (!e.matches) setSidebarOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isDesktop) setSidebarOpen(false);  // phone: navigating closes the drawer
  }, [location.pathname, isDesktop]);

  const toggleSidebar = () => {
    setSidebarOpen((open) => {
      if (isDesktop) {
        try { localStorage.setItem('sidebarOpen', String(!open)); } catch { /* storage unavailable */ }
      }
      return !open;
    });
  };
  const [exchangeRate, setExchangeRate] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Fetch live exchange rate
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/exchange-rates/live`, { withCredentials: true });
        if (res.data?.rate) setExchangeRate(res.data);
      } catch (e) {
        // silent fail – header badge is optional
      }
    };
    fetchRate();
    const interval = setInterval(fetchRate, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, []);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Role-based navigation configuration
  const getNavigationItems = () => {
    const role = user?.role;
    
    // Base items everyone can see
    const baseItems = [
      { id: "Dashboard", key: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
      { id: "Reports", key: 'reports', href: '/reports', icon: FileText },
    ];
    
    // Accountant sees Dashboard, Reports, Zakot, Ajratmalar, Settings (+Analytics if granted)
    if (role === 'accountant') {
      const items = [...baseItems, { id: "Zakot", key: 'zakat', href: '/zakat', icon: Calculator }, { id: "Ajratmalar", key: 'allocations', href: '/allocations', icon: Wallet }, { id: "Qarzlar", key: 'debts', href: '/debts', icon: HandCoins },
        { id: "Invoice'lar", key: 'clientInvoices', href: '/client-invoices', icon: ReceiptText }, { id: "AKT'lar", key: 'akt', href: '/akt', icon: FileSignature }, { id: "Kutilayotgan daromad", key: 'incomeEstimates', href: '/income-estimates', icon: CalendarClock }];
      if (user?.can_view_analytics) items.push({ id: "Analytics", key: 'analytics', href: '/analytics', icon: BarChart3 });
      items.push({ id: "Settings", key: 'settings', href: '/settings', icon: Settings });
      return items;
    }

    // Employee sees Dashboard, Projects, Employees, Reports, Settings (+Analytics if granted)
    if (role === 'employee') {
      const items = [
        { id: "Dashboard", key: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
        { id: "Projects", key: 'projects', href: '/projects', icon: FolderKanban },
        { id: "Employees", key: 'employees', href: '/employees', icon: Users },
        { id: "Reports", key: 'reports', href: '/reports', icon: FileText },
        { id: "Zakot", key: 'zakat', href: '/zakat', icon: Calculator },
        { id: "Ajratmalar", key: 'allocations', href: '/allocations', icon: Wallet },
        { id: "Qarzlar", key: 'debts', href: '/debts', icon: HandCoins },
      ];
      if (user?.can_view_analytics) items.push({ id: "Analytics", key: 'analytics', href: '/analytics', icon: BarChart3 });
      items.push({ id: "Settings", key: 'settings', href: '/settings', icon: Settings });
      return items;
    }
    
    // Manager sees most things except Users
    const managerItems = [
      { id: "Dashboard", key: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
      { id: "Projects", key: 'projects', href: '/projects', icon: FolderKanban },
      { id: "Employees", key: 'employees', href: '/employees', icon: Users },
      { id: "Assignments", key: 'assignments', href: '/assignments', icon: Link2 },
      { id: "Income", key: 'income', href: '/income', icon: DollarSign },
      { id: "Expenses", key: 'expenses', href: '/expenses', icon: Receipt },
      { id: "Income History", key: 'incomeHistory', href: '/income-history', icon: Calendar },
      { id: "Kutilayotgan daromad", key: 'incomeEstimates', href: '/income-estimates', icon: CalendarClock },
      { id: "Invoice'lar", key: 'clientInvoices', href: '/client-invoices', icon: ReceiptText },
      { id: "AKT'lar", key: 'akt', href: '/akt', icon: FileSignature },
      { id: "Reports", key: 'reports', href: '/reports', icon: FileText },
      { id: "Zakot", key: 'zakat', href: '/zakat', icon: Calculator },
      { id: "Ajratmalar", key: 'allocations', href: '/allocations', icon: Wallet },
      { id: "Qarzlar", key: 'debts', href: '/debts', icon: HandCoins },
      { id: "Analytics", key: 'analytics', href: '/analytics', icon: BarChart3 },
      { id: "Activity Logs", key: 'activity', href: '/activity', icon: Activity },
      { id: "Settings", key: 'settings', href: '/settings', icon: Settings },
    ];
    
    // Admin/Super Admin sees everything including Users
    if (role === 'super_admin' || role === 'admin') {
      const adminItems = [...managerItems];
      // Insert Users after Ajratmalar (find by href for resilience)
      const allocIdx = adminItems.findIndex(i => i.href === '/allocations');
      adminItems.splice(allocIdx >= 0 ? allocIdx + 1 : adminItems.length - 2, 0, { id: "Users", key: 'users', href: '/users', icon: UserCog });
      // Salary payouts (employees' card numbers) after Reports
      const reportsIdx = adminItems.findIndex(i => i.href === '/reports');
      adminItems.splice(reportsIdx + 1, 0, { id: "Payouts", key: 'payouts', href: '/payouts', icon: Banknote });
      return adminItems;
    }
    
    return managerItems;
  };

  const navigation = getNavigationItems();

  // Check if current user can access the current route
  const isRestrictedRoute = () => {
    const role = user?.role;
    const path = location.pathname;
    
    if (role === 'accountant') {
      // Accountant can access dashboard, reports, zakat, allocations and settings (+analytics if granted)
      const allowedPaths = ['/dashboard', '/reports', '/zakat', '/allocations', '/debts', '/client-invoices', '/akt', '/income-estimates', '/settings'];
      if (user?.can_view_analytics) allowedPaths.push('/analytics');
      return !allowedPaths.some(p => path.startsWith(p));
    }

    if (role === 'employee') {
      // Employee can access dashboard, projects, employees, reports, zakat, allocations, debts and settings (+analytics if granted)
      const allowedPaths = ['/dashboard', '/projects', '/employees', '/reports', '/zakat', '/allocations', '/debts', '/settings'];
      if (user?.can_view_analytics) allowedPaths.push('/analytics');
      return !allowedPaths.some(p => path.startsWith(p));
    }
    
    // Users and Payouts pages restricted to admin/super_admin
    if ((path.startsWith('/users') || path.startsWith('/payouts')) && role !== 'super_admin' && role !== 'admin') {
      return true;
    }
    
    return false;
  };

  // Redirect if accessing restricted route
  if (isRestrictedRoute()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Phone: dim the page behind the open drawer; tapping it closes the drawer */}
      {sidebarOpen && !isDesktop && (
        <button
          type="button"
          aria-label={t('layout.closeMenu')}
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-[1px] md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-[100dvh] transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 w-64`}
        data-testid="sidebar"
        aria-hidden={!sidebarOpen}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-slate-50 dark:from-slate-800 dark:to-slate-900">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-slate-700 dark:from-blue-400 dark:to-slate-300 bg-clip-text text-transparent" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
              FinOps
            </h1>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.key}
                  to={item.href}
                  data-testid={`nav-${item.id.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-sm font-medium">{t(`nav.${item.key}`)}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info & logout */}
          <div className="border-t border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={user?.picture ? (user.picture.startsWith('/uploads') ? `${API_URL}${user.picture}` : user.picture) : 'https://via.placeholder.com/40'}
                alt={user?.name}
                className="h-10 w-10 rounded-full object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {user?.role ? t(`roles.${user.role}`, { defaultValue: user.role.replace('_', ' ') }) : ''}
                </p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="w-full justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('layout.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`min-w-0 transition-[padding] duration-300 ${sidebarOpen ? 'md:pl-64' : ''}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-700/50">
          <div className="flex items-center justify-between h-16 px-3 sm:px-6 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? t('layout.closeMenu') : t('layout.openMenu')}
              aria-expanded={sidebarOpen}
              data-testid="toggle-sidebar-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Ramadan prayer times + live clock */}
            {/* Live clock */}
            {(() => {
              const timeNow = currentTime.toLocaleTimeString('en-GB', {
                timeZone: 'Asia/Tashkent', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
              });
              return (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/40 shadow-sm">
                  <Clock className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
                  <span className="text-sm font-mono font-bold text-slate-600 dark:text-slate-300">{timeNow}</span>
                </div>
              );
            })()}

            <div className="flex items-center gap-1 sm:gap-3">
              {/* Live Exchange Rate Badge */}
              {exchangeRate?.rate && (
                <Link
                  to="/settings"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors cursor-pointer"
                  title={exchangeRate.source === 'cbu' ? t('layout.rateCbu') : t('layout.rateManual')}
                >
                  <DollarSign className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                    1 USD = {Number(exchangeRate.rate).toLocaleString()} UZS
                  </span>
                  {exchangeRate.source === 'cbu' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" title={t('layout.rateLive')} />
                  )}
                </Link>
              )}

              <LanguageSwitcher />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                aria-label={t('layout.theme')}
                data-testid="theme-toggle-btn"
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </header>

        {/* Impersonation banner */}
        {user?.is_impersonating && (
          <div className="sticky top-16 z-20 flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-2.5 bg-amber-400 dark:bg-amber-500 text-amber-950 text-sm font-medium shadow-md">
            <div className="flex items-center gap-2">
              <UserCog className="h-4 w-4 shrink-0" />
              <span>
                <Trans i18nKey="layout.impersonating" values={{ name: user.name || user.email }} components={{ strong: <strong /> }} />
                {user.impersonated_by && (
                  <span className="font-normal opacity-80"> · {t('layout.impersonatedBy', { name: user.impersonated_by.name || user.impersonated_by.email })}</span>
                )}
              </span>
            </div>
            <button
              onClick={stopImpersonation}
              className="shrink-0 px-3 py-1 rounded-md bg-amber-950/15 hover:bg-amber-950/25 transition-colors text-xs font-semibold"
            >
              {t('layout.backToAdmin')}
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="min-w-0 p-3 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
